import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover } from 'radix-ui';
import { ChangeEvent, HTMLAttributes, ReactNode, useState } from 'react';

interface Option {
  id: string;
  label: string;
}

interface SelectProps extends HTMLAttributes<HTMLInputElement> {
  labelIcon?: ReactNode;
  label: string;
  options: Option[];
  placeholder: string;
  searchPlaceholder?: string;
}

export const Select = ({
  labelIcon,
  label,
  options,
  placeholder,
  searchPlaceholder,
}: SelectProps) => {
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value.toLowerCase();
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm)
    );
    setFilteredOptions(filtered);
  };

  const handleSelectOptions = (option: Option) => {
    if (selectedOptions.find(({ id }) => id === option.id))
      setSelectedOptions(selectedOptions.filter(({ id }) => id !== option.id));
    else setSelectedOptions([...selectedOptions, option]);
  };

  return (
    <div className="space-y-3">
      <label
        data-slot="label"
        className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
      >
        {labelIcon}
        {label}
      </label>

      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            data-slot="popover-trigger"
            className="w-auto hover:cursor-pointer border-border inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg:not([className*='size-'])]:size-4 shrink-0 [&amp;_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border bg-background text-foreground hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 px-4 has-[&gt;svg]:px-3 w-full justify-between min-h-10 h-auto py-2"
            role="combobox"
            type="button"
            aria-haspopup="dialog"
            aria-expanded="false"
            aria-controls="radix-:r18r:"
            data-state="closed"
          >
            <div className="flex flex-wrap gap-1 flex-1 truncate">
              {!selectedOptions.length && (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
              {selectedOptions.length <= 3 &&
                selectedOptions.map(({ label }, index) => (
                  <span
                    key={index}
                    data-slot="badge"
                    className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-medium w-fit whitespace-nowrap shrink-0 [&amp;&gt;svg]:size-3 gap-1 [&amp;&gt;svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground [a&amp;]:hover:bg-secondary/90 text-xs"
                  >
                    {label}
                  </span>
                ))}
              {selectedOptions.length > 3 && (
                <span
                  data-slot="badge"
                  className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-medium w-fit whitespace-nowrap shrink-0 [&amp;&gt;svg]:size-3 gap-1 [&amp;&gt;svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground [a&amp;]:hover:bg-secondary/90 text-xs"
                >
                  {selectedOptions.length} selecionados
                </span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content sideOffset={4}>
            <div className="bg-card border border-border rounded-lg w-54 shadow">
              <div className="w-full p-2 border-border border-b flex gap-2 items-center">
                <Search className="h-4 w-4" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  onChange={handleSearch}
                  className="outline-none w-auto h-5 text-xs"
                />
              </div>
              <div className="max-h-80 overflow-y-auto ">
                {filteredOptions.map((option, index) => (
                  <Popover.Close
                    key={index}
                    onClick={() => handleSelectOptions(option)}
                    className="text-xs hover:cursor-pointer rounded-lg hover:bg-accent dark:hover:bg-accent/50 hover:text-accent-foreground p-2 w-full flex justify-start items-center gap-4"
                    aria-label="Close"
                  >
                    {selectedOptions.includes(option) ? (
                      <Check className="text-muted-foreground h-4 w-4" />
                    ) : (
                      <div className="w-4" />
                    )}
                    {option.label}
                  </Popover.Close>
                ))}
                {!filteredOptions.length && (
                  <div className="p-2 h-20 text-sm flex items-center">
                    Nenhum resultado encontrado.
                  </div>
                )}
              </div>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
